import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../../app/AuthLayout';
import { Button } from '../../ui/Button';
import { Field, PasswordInput } from '../../ui/Form';
import { Alert } from '../../ui/Feedback';
import { PasswordRules, isStrongPassword } from './PasswordRules';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { apiRequest } from '../../lib/api';

export function ResetPasswordPage() {
  const t = useStrings();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useDocumentTitle(t('auth.resetTitle'));

  const mismatch = form.confirm.length > 0 && form.password !== form.confirm;

  const submit = async (event) => {
    event.preventDefault();
    if (!isStrongPassword(form.password)) {
      setError(t('auth.passwordWeak'));
      return;
    }
    if (form.password !== form.confirm) {
      setError(t('auth.passwordsMismatch'));
      return;
    }
    setSubmitting(true);
    setError(null);
    const response = await apiRequest('/auth/v2/reset-password', { method: 'POST', body: { token, newPassword: form.password } });
    setSubmitting(false);
    if (!response.success) {
      setError(response.error);
      return;
    }
    navigate('/login', { replace: true, state: { notice: { tone: 'success', message: t('auth.resetDone') } } });
  };

  if (!token) {
    return (
      <AuthLayout
        title={t('auth.resetTitle')}
        footer={
          <Link to="/login" className="text-link">
            {t('auth.backToSignIn')}
          </Link>
        }
      >
        <Alert tone="danger">{t('auth.resetMissingToken')}</Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.resetTitle')} subtitle={t('auth.resetText')}>
      {error && <Alert tone="danger">{error}</Alert>}
      <form className="auth__form" onSubmit={submit} noValidate>
        <Field label={t('auth.newPassword')} htmlFor="reset-password">
          <PasswordInput
            id="reset-password"
            size="lg"
            autoComplete="new-password"
            autoFocus
            aria-describedby="reset-rules"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            showLabel={t('auth.showPassword')}
            hideLabel={t('auth.hidePassword')}
          />
        </Field>
        <PasswordRules value={form.password} id="reset-rules" />
        <Field label={t('auth.confirmPassword')} htmlFor="reset-confirm" error={mismatch ? t('auth.passwordsMismatch') : null}>
          <PasswordInput
            id="reset-confirm"
            size="lg"
            autoComplete="new-password"
            invalid={mismatch}
            value={form.confirm}
            onChange={(event) => setForm({ ...form, confirm: event.target.value })}
            showLabel={t('auth.showPassword')}
            hideLabel={t('auth.hidePassword')}
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" block loading={submitting} disabled={!form.password || !form.confirm}>
          {submitting ? t('auth.savingPassword') : t('auth.savePassword')}
        </Button>
      </form>
    </AuthLayout>
  );
}
